import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Eye, FileCheck, Timer, Gauge, ArrowRight, Inbox,
  TrendingUp, Calendar, Mail, Phone, Sparkles, Globe, Activity, Webhook,
} from "lucide-react";
import { accessApi } from "@/access/api";

/* Enterprise-grade dashboard overview — KPI strip, form-mix breakdown,
   live activity feed, top pages, geo signals. */

const fmtSec = (s) => {
  if (!s) return "0s";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const FORM_META = {
  lead:               { label: "Demo requests",  Icon: Calendar,    accent: "violet"   },
  build_os:           { label: "Build OS",       Icon: Sparkles,    accent: "blue"     },
  audit:              { label: "Clinic audits",  Icon: Activity,    accent: "amber"    },
  integration_notify: { label: "EMR notifies",   Icon: Webhook,     accent: "fuchsia"  },
  newsletter:         { label: "Newsletter",     Icon: Mail,        accent: "sky"      },
  support:            { label: "Support",        Icon: Phone,       accent: "emerald"  },
};

const ACCENT_DOT = {
  violet:  "bg-violet-400",
  blue:    "bg-blue-400",
  amber:   "bg-amber-400",
  fuchsia: "bg-fuchsia-400",
  sky:     "bg-sky-400",
  emerald: "bg-emerald-400",
};
const ACCENT_BAR = {
  violet:  "from-violet-500 to-fuchsia-500",
  blue:    "from-blue-500 to-sky-500",
  amber:   "from-amber-500 to-orange-500",
  fuchsia: "from-fuchsia-500 to-pink-500",
  sky:     "from-sky-500 to-cyan-500",
  emerald: "from-emerald-500 to-teal-500",
};
const ACCENT_TEXT = {
  violet:  "text-violet-300",
  blue:    "text-blue-300",
  amber:   "text-amber-300",
  fuchsia: "text-fuchsia-300",
  sky:     "text-sky-300",
  emerald: "text-emerald-300",
};

export default function AccessOverview() {
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState({ total: 0, by_form: {} });
  const [subs, setSubs] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      accessApi.analytics(days),
      accessApi.submissions({ limit: 8 }),
      accessApi.submissionCounts(),
    ])
      .then(([a, s, c]) => {
        if (!alive) return;
        setData(a.data);
        setSubs(s.data.slice(0, 8));
        setCounts(c.data);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [days]);

  return (
    <div data-testid="access-overview" className="space-y-8">
      {/* Page header */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-violet-300">Overview</p>
          <h1 className="mt-1.5 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Good morning.
          </h1>
          <p className="mt-1.5 text-[14px] leading-relaxed text-white/55">
            Site health, lead flow and audience signals — all in one view.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/40">Range</span>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="overview-range"
            className="rounded-lg border border-white/[0.08] bg-white/[0.04] px-3 py-1.5 text-[12.5px] font-bold focus:border-violet-400/60 focus:outline-none"
          >
            {[7, 14, 30, 60, 90].map((d) => (
              <option key={d} value={d} className="bg-[#0a0a14]">Last {d} days</option>
            ))}
          </select>
        </div>
      </header>

      {/* KPI strip — 5 enterprise stat cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi Icon={Users}     label="Unique visitors"  value={data?.sessions ?? "—"}            hint="Distinct browser sessions" loading={loading} accent="violet" />
        <Kpi Icon={Eye}       label="Pageviews"        value={data?.pageviews ?? "—"}           hint="All pages combined"        loading={loading} accent="blue" />
        <Kpi Icon={FileCheck} label="Submissions"      value={counts.total}                     hint="Every form"                loading={loading} accent="emerald" />
        <Kpi Icon={Timer}     label="Avg. session"     value={fmtSec(data?.avg_session_seconds)} hint="Time on site"             loading={loading} accent="fuchsia" />
        <Kpi Icon={Gauge}     label="Avg. load time"   value={`${data?.avg_load_ms ?? 0} ms`}    hint="P50 across pages"         loading={loading} accent="amber" />
      </div>

      {/* Submission breakdown — wide enterprise card */}
      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.025] p-6 backdrop-blur-sm">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/55">Submissions by form</p>
            <h2 className="mt-1.5 font-display text-xl font-black tracking-tight sm:text-2xl">
              {counts.total} {counts.total === 1 ? "submission" : "submissions"} captured
            </h2>
            <p className="mt-1 text-[12.5px] text-white/45">
              Every form on the site flows here. Click a row to filter.
            </p>
          </div>
          <Link
            to="/access/submissions"
            data-testid="overview-view-all-subs"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.1] bg-white/[0.03] px-3 py-1.5 text-[12px] font-bold hover:bg-white/[0.06]"
          >
            View raw log <ArrowRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="mt-6 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(FORM_META).map(([key, meta]) => {
            const n = counts.by_form?.[key] || 0;
            const pct = counts.total ? Math.round((n / counts.total) * 100) : 0;
            return (
              <Link
                key={key}
                to={`/access/submissions?form=${key}`}
                data-testid={`overview-form-${key}`}
                className="group flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5 transition-all hover:-translate-y-0.5 hover:border-white/[0.14] hover:bg-white/[0.04]"
              >
                <span className={`inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${ACCENT_BAR[meta.accent]} text-white shadow-md`}>
                  <meta.Icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-[13.5px] font-black tracking-tight">{meta.label}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="h-1 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
                      <span className={`block h-full bg-gradient-to-r ${ACCENT_BAR[meta.accent]} transition-all`} style={{ width: `${Math.max(pct, n ? 6 : 0)}%` }} />
                    </span>
                    <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/40">{pct}%</span>
                  </div>
                </div>
                <span className="font-display text-2xl font-black tracking-tight tabular-nums">{n}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Two-column: activity feed + top pages */}
      <div className="grid gap-4 lg:grid-cols-[1.4fr,1fr]">
        {/* Activity feed */}
        <section className="rounded-2xl border border-white/[0.06] bg-white/[0.025] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/55">Recent activity</p>
              <h2 className="mt-1.5 font-display text-xl font-black tracking-tight">Latest 8 submissions</h2>
            </div>
            <Link
              to="/access/leads"
              className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-violet-300 hover:text-violet-200"
            >
              Sales board <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          <ul className="mt-5 divide-y divide-white/[0.06]">
            {subs.map((s) => {
              const meta = FORM_META[s.form_type] || { label: s.form_type, Icon: Inbox, accent: "violet" };
              return (
                <li key={s.id} className="flex items-start gap-3 py-3">
                  <span className={`inline-flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-gradient-to-br ${ACCENT_BAR[meta.accent]} text-white shadow-sm`}>
                    <meta.Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-[13.5px] font-bold tracking-tight">
                      {s.data?.name || s.data?.email || "(anonymous)"}
                    </p>
                    <p className="mt-0.5 truncate text-[11.5px] text-white/50">
                      <span className={`${ACCENT_TEXT[meta.accent]} font-bold`}>{meta.label}</span>
                      {" · "}
                      {s.attribution?.page || "—"}
                      {s.attribution?.utm_source && (
                        <> {" · "}<span className="text-violet-300">{s.attribution.utm_source}</span></>
                      )}
                    </p>
                  </div>
                  <span className="flex flex-col items-end gap-1">
                    <span className={`inline-flex rounded-md px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.14em] ${statusBadge(s.status)}`}>
                      {s.status}
                    </span>
                    <span className="font-mono text-[10.5px] text-white/35">
                      {new Date(s.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </span>
                </li>
              );
            })}
            {!loading && subs.length === 0 && (
              <li className="py-8 text-center text-[12.5px] text-white/40">
                No submissions yet — your first form fill will land here.
              </li>
            )}
          </ul>
        </section>

        {/* Top pages */}
        <section className="rounded-2xl border border-white/[0.06] bg-white/[0.025] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/55">Top pages</p>
              <h2 className="mt-1.5 font-display text-xl font-black tracking-tight">Where they land</h2>
            </div>
            <Link to="/access/analytics" className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-violet-300 hover:text-violet-200">
              All analytics <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          <ul className="mt-5 space-y-2.5">
            {(data?.top_pages || []).slice(0, 7).map((p, i) => {
              const max = data.top_pages[0]?.views || 1;
              const pct = Math.round((p.views / max) * 100);
              return (
                <li key={p.path}>
                  <div className="flex items-center gap-2 text-[12.5px]">
                    <span className="w-4 flex-none font-mono text-[10.5px] text-white/35">{i + 1}</span>
                    <span className="flex-1 truncate font-mono text-white/85">{p.path}</span>
                    <span className="font-bold tabular-nums text-white">{p.views}</span>
                  </div>
                  <span className="mt-1 block h-1 overflow-hidden rounded-full bg-white/[0.05]">
                    <span className="block h-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${pct}%` }} />
                  </span>
                </li>
              );
            })}
            {!loading && (data?.top_pages || []).length === 0 && (
              <li className="py-6 text-center text-[12px] text-white/40">
                No traffic yet — pageviews populate as visitors browse.
              </li>
            )}
          </ul>
        </section>
      </div>

      {/* Audience signals strip */}
      {data && (data.countries.length > 0 || data.devices.length > 0) && (
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SignalCard Icon={Globe}      title="Top country"       value={data.countries[0]?.name || "—"} sub={data.countries[0] ? `${data.countries[0].n} pageviews` : ""} />
          <SignalCard Icon={TrendingUp} title="Top channel (UTM)" value={data.submission_pages?.[0]?.path || data.top_pages[0]?.path || "—"} sub="Top converting page" mono />
          <SignalCard Icon={Activity}   title="Conversion rate"   value={data.sessions ? `${((counts.total / data.sessions) * 100).toFixed(1)}%` : "—"} sub="Submissions ÷ sessions" />
        </section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function Kpi({ Icon, label, value, hint, loading, accent }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.025] p-5 backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-white/[0.12]">
      <div className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${ACCENT_BAR[accent]} opacity-[0.08] blur-2xl`} />
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br ${ACCENT_BAR[accent]} text-white shadow-sm`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/55">{label}</p>
      </div>
      <p className="mt-4 font-display text-[28px] font-black tracking-tight tabular-nums leading-none sm:text-[32px]">
        {loading ? <span className="inline-block h-7 w-12 animate-pulse rounded bg-white/10" /> : value}
      </p>
      <p className="mt-2 text-[11px] text-white/40">{hint}</p>
    </div>
  );
}

function SignalCard({ Icon, title, value, sub, mono }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.025] p-5">
      <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/55">
        <Icon className="h-3.5 w-3.5 text-violet-300" /> {title}
      </p>
      <p className={`mt-3 font-display text-xl font-black tracking-tight truncate ${mono ? "font-mono" : ""}`}>
        {value}
      </p>
      {sub && <p className="mt-1 text-[11.5px] text-white/45">{sub}</p>}
    </div>
  );
}

function statusBadge(s) {
  switch (s) {
    case "new":       return "bg-violet-500/20 text-violet-200";
    case "contacted": return "bg-amber-500/20 text-amber-200";
    case "qualified": return "bg-sky-500/20 text-sky-200";
    case "resolved":  return "bg-emerald-500/20 text-emerald-200";
    default:          return "bg-white/10 text-white/70";
  }
}
